import { Injectable } from '@angular/core';

import { CatalogObject, PcvizProject, PcvizProjectEditionTool } from '@mirage-projects/cw-models';

import { NotificationService, NotificationTopics } from '../notification/notification.service';
import { ToastService } from '../toast/toast.service';
import { TocManagementService } from '../toc/toc-management.service';
import { TranslationService } from '../translate/translation.service';

@Injectable({ providedIn: 'root' })
export class ProjectEditionService {
    constructor(
        private toastService: ToastService,
        private notificationService: NotificationService,
        private translationService: TranslationService,
        private tocManagementService: TocManagementService
    ) {}

    /**
     * Adds a layer to an existing project
     * @param project Project to add the layer
     * @param layerId Id of the layer to be added
     * @param parentLayerId Layer which should be the parent of the new layer (if any)
     * @param projectEditionTool Tool to enable adding the layer
     */
    async addLayer(
        project: PcvizProject,
        layerId: number,
        parentLayerId: number | undefined,
        projectEditionTool: PcvizProjectEditionTool
    ): Promise<number | boolean> {
        const isLayerInProject = !!project?.getLayerById(layerId);

        if (isLayerInProject) {
            this.toastService.error('TAB_LAYERS.LAYER_EXIST');
            return false;
        }

        try {
            await projectEditionTool.addLayer(layerId, parentLayerId);

            this.tocManagementService.reloadToc();
            this.notificationService.addTopicNotification(`${NotificationTopics.LAYERS}${layerId}`);

            return layerId;
        } catch (errorResponse) {
            const errorMsg = this.translationService.translate('TAB_LAYERS.ADD_LAYER_FAILED');
            this.toastService.error(`${errorMsg} ${errorResponse ?? ''}`);
            return false;
        }
    }

    /**
     * Adds a list of layers to an existing project
     * @param project Project to add the layer
     * @param layers List of layers to be added
     * @param parentLayerId Layer which should be the parent of the new layer (if any)
     * @param projectEditionTool Tool to enable adding the layer
     */
    async addLayers(
        project: PcvizProject,
        layers: CatalogObject[],
        parentLayerId: number | undefined,
        projectEditionTool: PcvizProjectEditionTool
    ): Promise<boolean> {
        const layersIds = layers.map((item) => item.id);

        return this.addLayersById(project, layersIds, parentLayerId, projectEditionTool);
    }

    /**
     * Adds a list of layers denoted by their id's  to an existing project
     * @param project Project to add the layer
     * @param layersId List of layer id's to be added
     * @param parentLayerId Layer which should be the parent of the new layer (if any)
     * @param projectEditionTool Tool to enable adding the layer
     */
    async addLayersById(
        project: PcvizProject,
        layersId: number[],
        parentLayerId: number | undefined,
        projectEditionTool: PcvizProjectEditionTool
    ): Promise<boolean> {
        if (!layersId.length) {
            return false;
        }

        const addedLayers = await Promise.all(
            layersId.map(async (layer) => await this.addLayer(project, layer, parentLayerId, projectEditionTool))
        );

        const areValidLayersAdded = addedLayers.some((layer) => !!layer);

        return areValidLayersAdded;
    }
}
